/**
 * Champ de recherche de ville (géocodage Open-Meteo, couverture mondiale) avec
 * autocomplétion débouncée. À la sélection, renvoie le résultat (lat/lon + nom).
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import {
  countryCodeToFlag,
  formatGeoRegion,
  formatGeoResult,
  GeoResult,
  searchCities,
} from "../../../api/weather";
import { useDebounce } from "../../../hooks/useDebounce";
import { widgetSelectClassName } from "./AddWidgetLayout";

type CitySearchFieldProps = {
  value?: string;
  onSelect: (geo: GeoResult) => void;
};

export function CitySearchField({ value, onSelect }: CitySearchFieldProps) {
  const [query, setQuery] = useState(value ?? "");
  const [results, setResults] = useState<GeoResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounced = useDebounce(query, 400);
  // Évite de relancer une recherche juste après une sélection (qui modifie query).
  const skipNextSearch = useRef(false);
  // Ne déclenche la recherche (et l'ouverture de la liste) que si l'utilisateur a
  // tapé — sinon la valeur pré-remplie (ville actuelle) ouvrirait la liste au montage.
  const hasTyped = useRef(false);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    if (!hasTyped.current) return;

    const q = debounced.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    searchCities(q, controller.signal)
      .then((found) => {
        setResults(found);
        setOpen(true);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [debounced]);

  const handleSelect = (geo: GeoResult) => {
    skipNextSearch.current = true;
    setQuery(formatGeoResult(geo));
    setResults([]);
    setOpen(false);
    onSelect(geo);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          className={`${widgetSelectClassName} pr-10`}
          value={query}
          placeholder="Rechercher une ville…"
          onChange={(event) => {
            hasTyped.current = true;
            setQuery(event.target.value);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
        />
        <FontAwesomeIcon
          icon="magnifying-glass"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-grey-medium pointer-events-none"
        />
      </div>

      {loading && <p className="text-xs text-grey-medium mt-1">Recherche…</p>}

      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-grey-light rounded-lg shadow-lg max-h-60 overflow-auto py-1">
          {results.map((geo) => (
            <li key={geo.id}>
              <button
                type="button"
                onClick={() => handleSelect(geo)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-grey-light/40 transition-colors"
              >
                <span className="text-lg leading-none shrink-0">
                  {countryCodeToFlag(geo.countryCode)}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-default-text truncate">
                    {geo.name}
                  </span>
                  <span className="block text-xs text-grey-medium truncate">
                    {formatGeoRegion(geo)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
